import React, { useEffect, useRef } from 'react';
import {
  AlertCircle,
  Check,
  Circle,
  LoaderCircle,
} from 'lucide-react';

const STAGES = [
  { key: 'upload', label: 'Upload' },
  { key: 'segmentation', label: 'Segmentation' },
  { key: 'analysis', label: 'Analysis' },
  { key: 'playerCards', label: 'Player Cards' },
  { key: 'complete', label: 'Complete' },
];

function formatTimestamp(value) {
  if (!value) {
    return '--:--:--';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleTimeString();
}

function getStageIndex(status, progress) {
  if (status === 'submitted') {
    return 0;
  }

  if (status === 'triggering') {
    return 1;
  }

  if (status === 'processing') {
    return progress >= 60 ? 3 : 2;
  }

  if (status === 'complete') {
    return 4;
  }

  if (status === 'error') {
    if (progress >= 60) {
      return 3;
    }

    if (progress >= 10) {
      return 2;
    }

    return 1;
  }

  return 0;
}

function getStageState(stageIndex, activeIndex, status) {
  if (status === 'complete') {
    return 'complete';
  }

  if (status === 'error') {
    if (stageIndex < activeIndex) {
      return 'complete';
    }

    if (stageIndex === activeIndex) {
      return 'error';
    }

    return 'pending';
  }

  if (stageIndex < activeIndex) {
    return 'complete';
  }

  if (stageIndex === activeIndex) {
    return 'active';
  }

  return 'pending';
}

function getStageStyles(stageState) {
  if (stageState === 'complete') {
    return {
      circleClass: 'border-emerald-500 bg-emerald-500/15 text-emerald-400',
      lineClass: 'bg-emerald-500/60',
      labelClass: 'text-emerald-300',
    };
  }

  if (stageState === 'active') {
    return {
      circleClass: 'border-[#E8B800] bg-[#E8B800]/12 text-[#E8B800] animate-pulse',
      lineClass: 'bg-[#E8B800]/35',
      labelClass: 'text-[#E8B800]',
    };
  }

  if (stageState === 'error') {
    return {
      circleClass: 'border-red-500 bg-red-500/12 text-red-400',
      lineClass: 'bg-red-500/30',
      labelClass: 'text-red-300',
    };
  }

  return {
    circleClass: 'border-zinc-700 bg-[#111111] text-zinc-500',
    lineClass: 'bg-zinc-800',
    labelClass: 'text-zinc-500',
  };
}

function renderStageIcon(stageState, index) {
  if (stageState === 'complete') {
    return <Check size={15} />;
  }

  if (stageState === 'active') {
    return <LoaderCircle size={15} className="animate-spin" />;
  }

  if (stageState === 'error') {
    return <AlertCircle size={15} />;
  }

  return <Circle size={14} />;
}

function getTraceColor(traceStatus) {
  if (traceStatus === 'complete') {
    return 'text-emerald-400';
  }

  if (traceStatus === 'running') {
    return 'text-[#E8B800]';
  }

  if (traceStatus === 'error') {
    return 'text-red-400';
  }

  return 'text-zinc-400';
}

export default function PipelineTracer({
  jobId,
  status,
  progress,
  agentTrace,
  message,
}) {
  const logContainerRef = useRef(null);
  const activeIndex = getStageIndex(status, progress);

  useEffect(() => {
    console.log('[DropZone:Tracer] render update', {
      jobId,
      status,
      progress,
      traceCount: agentTrace?.length || 0,
      message,
    });
  }, [jobId, status, progress, agentTrace, message]);

  useEffect(() => {
    if (!logContainerRef.current) {
      return;
    }

    logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
  }, [agentTrace]);

  return (
    <div className="rounded-3xl border border-[#1A1A1A] bg-[#0A0A0A] p-5">
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-zinc-500">
            Pipeline Tracer
          </div>
          <div className="mt-1 text-lg font-semibold text-white">
            Job {jobId?.slice(0, 8) || 'pending'}
          </div>
        </div>
        <div className="rounded-full border border-[#1A1A1A] bg-[#111111] px-3 py-1 text-xs font-medium uppercase tracking-[0.18em] text-zinc-300">
          {status || 'pending'}
        </div>
      </div>

      <div className="mt-6 flex items-start justify-between gap-2 overflow-x-auto pb-2">
        {STAGES.map((stage, index) => {
          const stageState = getStageState(index, activeIndex, status);
          const styles = getStageStyles(stageState);

          return (
            <React.Fragment key={stage.key}>
              <div className="flex min-w-[84px] flex-1 flex-col items-center text-center">
                <div
                  className={[
                    'flex h-11 w-11 items-center justify-center rounded-full border transition-all duration-200',
                    styles.circleClass,
                  ].join(' ')}
                >
                  {renderStageIcon(stageState, index)}
                </div>
                <div className={`mt-3 text-xs font-medium uppercase tracking-[0.18em] ${styles.labelClass}`}>
                  {stage.label}
                </div>
              </div>

              {index < STAGES.length - 1 ? (
                <div className="mt-5 h-[2px] flex-1 min-w-[28px] rounded-full bg-zinc-800">
                  <div
                    className={`h-full rounded-full transition-all duration-300 ${styles.lineClass}`}
                    style={{
                      width: index < activeIndex || status === 'complete' ? '100%' : '0%',
                    }}
                  />
                </div>
              ) : null}
            </React.Fragment>
          );
        })}
      </div>

      <div className="mt-6">
        <div className="flex items-center justify-between text-xs uppercase tracking-[0.18em] text-zinc-500">
          <span>{message || 'Waiting for pipeline update'}</span>
          <span>{progress || 0}%</span>
        </div>
        <div className="mt-2 h-2 overflow-hidden rounded-full bg-[#111111]">
          <div
            className={[
              'h-full rounded-full transition-all duration-300',
              status === 'error'
                ? 'bg-gradient-to-r from-red-500 to-red-400'
                : status === 'complete'
                  ? 'bg-gradient-to-r from-emerald-500 to-emerald-400'
                  : 'bg-gradient-to-r from-[#E8B800] to-amber-400',
            ].join(' ')}
            style={{ width: `${progress || 0}%` }}
          />
        </div>
      </div>

      <div className="mt-6">
        <div className="mb-3 text-xs uppercase tracking-[0.22em] text-zinc-500">
          Agent Trace
        </div>
        <div
          ref={logContainerRef}
          className="max-h-72 overflow-y-auto rounded-2xl border border-[#1A1A1A] bg-[#050505] p-3"
        >
          {agentTrace?.length ? (
            <div className="space-y-2">
              {agentTrace.map((trace, index) => (
                <div
                  key={`${trace.ts || 'trace'}-${index}`}
                  className="rounded-xl border border-white/[0.04] bg-[#0A0A0A] px-3 py-2 text-xs"
                >
                  <span className="text-zinc-500">[{formatTimestamp(trace.ts)}]</span>{' '}
                  <span className="text-zinc-300">[{trace.stage || 'pipeline'}]</span>{' '}
                  <span className={`font-medium uppercase ${getTraceColor(trace.status)}`}>
                    {trace.status || 'pending'}
                  </span>{' '}
                  <span className="text-zinc-400">- {trace.detail || 'No detail provided'}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-zinc-500">
              Trace events will stream here as the six-lens analysis pipeline advances.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
